import { createFileRoute } from "@tanstack/react-router";
import { ResourceArticle } from "@/components/ResourceArticle";
import { buildBreadcrumbs, buildArticle } from "@/components/SchemaJsonLd";
import heroImg from "@/assets/flake-closeup.jpg";

export const Route = createFileRoute("/blog/concrete-coating-maintenance")({
  head: () => ({
    meta: [
      { title: "Long-Term Concrete Coating Maintenance | MLC Epoxy Floors" },
      { name: "description", content: "Simple care routines that keep an epoxy or polyaspartic floor looking new for 15+ years. Daily, monthly, and annual practices from Calgary Region coating pros." },
      { property: "og:title", content: "Long-Term Concrete Coating Maintenance" },
      { property: "og:description", content: "Simple care that keeps a coated floor looking new for 15+ years." },
      { property: "og:url", content: "/blog/concrete-coating-maintenance" },
      { property: "og:image", content: heroImg },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "/blog/concrete-coating-maintenance" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildArticle({
          title: "Long-Term Concrete Coating Maintenance",
          description: "Simple care routines that keep an epoxy or polyaspartic floor looking new for 15+ years. Daily, monthly, and annual practices from Calgary Region coating pros.",
          slug: "concrete-coating-maintenance",
          datePublished: "2026-01-16",
        })),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildBreadcrumbs([
          { name: "Home", href: "/" },
          { name: "Blog", href: "/blog" },
          { name: "Long-Term Concrete Coating Maintenance", href: "/blog/concrete-coating-maintenance" },
        ])),
      },
    ],
  }),
  component: () => (
    <ResourceArticle
      eyebrow="Jan 16, 2026"
      title="Long-Term Concrete Coating Maintenance"
      intro="A polyaspartic floor is built to last 15–20 years — but only if you treat it right. The good news: 'right' is much simpler than most people think."
      image={heroImg}
      backHref="/blog"
      backLabel="All Articles"
      sections={[
        {
          type: "prose",
          heading: "Why Maintenance Matters Less Than You'd Think",
          body: "Polyaspartic top coats are non-porous, UV-stable, and chemically resistant. That means no sealing, no waxing, and no specialty products. The single biggest threat to your floor isn't chemicals — it's grit. Sand, road salt, and small rocks act like sandpaper underfoot and under tires.",
        },
        {
          type: "list",
          heading: "The 5-Minute Weekly Routine",
          items: [
            { label: "Microfiber dust mop", desc: "Removes the abrasive grit before it has time to scratch the top coat." },
            { label: "Walk-off mats at the entry", desc: "Stops most debris before it reaches the coating." },
            { label: "Wipe visible spills", desc: "Won't damage the floor, but oil and brake fluid get tracked everywhere if left." },
          ],
        },
        {
          type: "list",
          heading: "Monthly Wet Clean",
          items: [
            { label: "Warm water + pH-neutral cleaner", desc: "Simple Green, mild dish soap, or any neutral floor cleaner. Skip vinegar and citrus-based cleaners." },
            { label: "Soft-bristle deck brush", desc: "Never steel wool or abrasive pads. They'll dull the gloss over years." },
            { label: "Squeegee toward the door or drain", desc: "Standing water won't hurt the floor but can leave mineral spots in hard-water areas." },
          ],
        },
        {
          type: "list",
          heading: "Once-A-Year Checks",
          items: [
            { label: "New cracks in the concrete", desc: "Structural cracks can telegraph through. Catch early — easy spot repair." },
            { label: "Edge lift at thresholds", desc: "Rare on properly prepped floors, but quick to fix if it happens." },
            { label: "Top-coat dulling in traffic lanes", desc: "After 10–15 years, a single refresher coat restores original gloss at a fraction of full install cost." },
          ],
        },
        {
          type: "prose",
          heading: "The Long View",
          body: "Floors we installed in 2010 are still going strong in 2026 — and the owners did nothing special. They swept, mopped occasionally, and parked their cars. That's it. A good coating system is designed to be lived on, not babied. Use the space the way you want; the floor will hold up.",
        },
        {
          type: "links",
          heading: "Related On This Site",
          intro: "Dig deeper into the systems behind a 15-year floor.",
          links: [
            { href: "/services/polyaspartic-floor-coatings", label: "Polyaspartic Floor Coatings", desc: "The UV-stable top coat doing the heavy lifting on every project." },
            { href: "/resources/garage-floor-coating-maintenance", label: "Full Maintenance Guide", desc: "Daily, weekly, and annual care in one reference." },
            { href: "/gallery?category=Residential", label: "Residential Gallery", desc: "See floors years into their service life." },
          ],
        },
      ]}
    />
  ),
});
